import { useSyncExternalStore } from 'react';
import type { AbsenceEditingLock } from '../types';

/** Topics on the shared application socket, matching ABSENCE_CHANGED_TOPIC
 * and ABSENCE_EDITING_TOPIC in apps/activity/services/absence/events.py. */
export const ABSENCE_CHANGED_TOPIC = 'absence.changed';
export const ABSENCE_EDITING_TOPIC = 'absence.editing';

const EMPTY: AbsenceEditingLock[] = [];

let snapshot: AbsenceEditingLock[] = EMPTY;
let lastReadAt = 0;
let connectionId = '';
const listeners = new Set<() => void>();

function publish() {
  listeners.forEach((listener) => {
    listener();
  });
}

/**
 * Record the id this tab claims its locks under.
 *
 * It changes on every (re)connection, and that change is the signal that any
 * lock held under the old one is gone: the server released it when the socket
 * closed. Readers treat it as "revalidate what you think you hold".
 */
export function setAbsenceConnectionId(next: string) {
  if (next === connectionId) return;
  connectionId = next;
  // The stamp belongs to the connection that issued it. A reconnect starts a
  // new sequence, and keeping the old high-water mark would drop the fresh
  // snapshot that arrives with it.
  lastReadAt = 0;
  publish();
}

export function getAbsenceConnectionId(): string {
  return connectionId;
}

/** The id this tab holds its locks under, or '' before the socket connects. */
export function useAbsenceConnectionId(): string {
  return useSyncExternalStore(
    subscribeToAbsenceEditing,
    getAbsenceConnectionId,
  );
}

/**
 * Replace the set of employees somebody has an edit form open on.
 *
 * Kept outside React because the controller that receives it is mounted in the
 * header while the only readers are on the absence page. An unchanged snapshot
 * is dropped rather than published, so the routine case -- nobody editing,
 * message after message -- re-renders nothing.
 */
export function setAbsenceEditingSnapshot(
  locks: AbsenceEditingLock[],
  readAt = 0,
) {
  // Reading and publishing are separate steps on the server, so an older
  // snapshot can arrive after a newer one. Applying it would leave this client
  // believing a lock is held that has been given back -- and since the
  // re-acquire is driven by this very state, nothing would come along to
  // correct it. A stamp of 0 means the server had no clock to read, so arrival
  // order is all there is and the snapshot is applied.
  if (readAt > 0) {
    if (readAt <= lastReadAt) return;
    lastReadAt = readAt;
  }

  const next = locks.length === 0 ? EMPTY : locks;
  if (next === snapshot) return;
  if (
    next.length === snapshot.length &&
    next.every((lock, index) => {
      const current = snapshot[index];
      return (
        current !== undefined &&
        current.targetUserId === lock.targetUserId &&
        current.ownerUserId === lock.ownerUserId &&
        current.ownerName === lock.ownerName
      );
    })
  ) {
    return;
  }

  snapshot = next;
  publish();
}

export function getAbsenceEditingSnapshot(): AbsenceEditingLock[] {
  return snapshot;
}

/** Forget the ordering stamp, for tests and for a fresh connection. */
export function resetAbsenceEditingOrdering() {
  lastReadAt = 0;
}

export function subscribeToAbsenceEditing(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The employees somebody currently has an edit form open on. */
export function useAbsenceEditing(): AbsenceEditingLock[] {
  return useSyncExternalStore(
    subscribeToAbsenceEditing,
    getAbsenceEditingSnapshot,
  );
}
